import {Period} from './leasing.model';

export function startDateMinValue<T extends Period>(item: T | null, allItems: T[] | Period[] | null, isEditing?: boolean): any {
  if (item != null && allItems != null) {
    const currentStartDate = isEditing ? item.startDate : new Date();
    const previousPeriod = findPreviousPricingPeriod(allItems, currentStartDate);
    if (previousPeriod) {
      const transactionDate = previousPeriod.latestTransactionDate && previousPeriod.startDate < previousPeriod.latestTransactionDate
        ? previousPeriod.startDate
        : previousPeriod.latestTransactionDate || previousPeriod.startDate;

      if (transactionDate) {
        const followingDay = new Date(transactionDate);
        followingDay.setDate(followingDay.getDate() + 1);
        return followingDay;
      }
    }
  }
  return '';
}

export function startDateMaxValue<T extends Period>(item: T | null, allItems: T[] | Period[] | null): any {
  if (item != null && allItems != null && item.endDate) {
    let followingPeriod = findFollowingPeriod(allItems, item.endDate);
    if (followingPeriod) {
      const transactionDate = followingPeriod.endDate && followingPeriod.earliestTransactionDate &&
      followingPeriod.endDate > followingPeriod.earliestTransactionDate ? followingPeriod?.earliestTransactionDate
        : followingPeriod.endDate || item.endDate;
      if (transactionDate) {
        const previousDay = new Date(transactionDate);
        previousDay.setDate(previousDay.getDate() - 1);
        return previousDay;
      }
    }
  }
  return '';
}

export function endDateMinValue<T extends Period>(item: T | null, allItems: T[] | Period[] | null): any {
  if (item != null && allItems != null) {
    let previousPeriod: T | null = findPreviousPricingPeriod(allItems, item.startDate!);
    const transactionDate = item.latestTransactionDate ||
      previousPeriod?.latestTransactionDate ||
      previousPeriod?.startDate ||
      item.startDate;

    if (transactionDate) {
      const followingDay = new Date(transactionDate);
      followingDay.setDate(followingDay.getDate() + 1);
      return followingDay;
    }
  }
  return '';
}

export function endDateMaxValue<T extends Period>(item: T | null, allItems: T[] | Period[] | null): any {
  if (item != null && allItems != null) {
    if (!item.endDate) return '';

    const followingPeriod = findFollowingPeriod(allItems, item.endDate);

    if (followingPeriod) {
      const transactionDate = followingPeriod.earliestTransactionDate || followingPeriod.endDate;

      if (transactionDate) {
        const previousDay = new Date(transactionDate);
        previousDay.setDate(previousDay.getDate() - 1);
        return previousDay;
      }
    }
  }

  return '';
}

export function findCurrentPeriod<T extends Period>(periods: T[]): T | null {
  const today = new Date();

  for (const period of periods) {
    const startDate = period.startDate ? convertToDateObject(period.startDate) : null;
    const endDate = period.endDate ? convertToDateObject(period.endDate) : null;


    if (
      (startDate === null || today >= startDate) &&
      (endDate === null || today <= endDate)
    ) {
      return period;
    }
  }

  return null;
}

export function findPreviousPricingPeriod<T extends Period>(allItems: T[] | null, startDate: Date | any): any | null {

  if (allItems == null) {
    return null;
  }
  // Filter periods that end before the provided startDate or have a null end date
  const precedingPeriods = allItems
    .filter(period => period != null && ((period.endDate && period.endDate < startDate) || period.endDate === null));

  // If there are no preceding periods, return null
  if (precedingPeriods.length === 0) {
    return null;
  }

  // Return the one with the latest endDate, prioritizing null end dates
  return precedingPeriods
    .filter(period => period != null)
    .reduce((latest, period) => {
      if (period!.endDate === null) {
        return period; // Prefer periods with null endDate
      }
      return !latest!.endDate || period!.endDate > latest!.endDate ? period : latest;
    }, precedingPeriods[0]);

}

// Function to find the following period
export function findFollowingPeriod<T extends Period>(allItems: T[] | null, endDate: Date): T | null {
  if (allItems == null) {
    return null;
  }
  // Filter periods that start after the provided endDate
  const followingPeriods = allItems.filter(period => period != null && period.startDate && period.startDate > endDate);

  // If there are no following periods, return null
  if (followingPeriods.length === 0) {
    return null;
  }

  // Return the one with the earliest startDate
  return followingPeriods.reduce((earliest, period) => {
    return period!.startDate && (!earliest!.startDate || period!.startDate < earliest!.startDate) ? period : earliest;
  }, followingPeriods[0]);
}


export function convertToDateObject(date: any): Date | null {
  if (date != null) {
    if (typeof date === 'number') {
      return new Date(date * 1000)
    }
    if (date instanceof Date) {
      return date;
    }
  }
  return null;
}
